import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import type { z } from 'zod';
import { api } from '../../api/client';
import { errorMessageKey, unwrap } from '../../api/api-error';
import { ensureOk } from '../../auth/session';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Notice } from '../../components/ui/notice';
import { TextField } from '../../components/ui/text-field';
import { useI18n } from '../../i18n/i18n';
import { can, type Workspace, workspaceKey, WORKSPACES_KEY } from '../../workspaces/workspaces-api';
import { useCurrentWorkspace } from '../../workspaces/current-workspace';
import { workspaceNameSchema } from './workspaces-page';
import { paths } from '../../app/paths';

type NameValues = z.infer<typeof workspaceNameSchema>;

/** Настройки пространства: имя, архив, выход — по правам из ответа API. */
export function OverviewPage() {
  const { t } = useI18n();
  const workspace = useCurrentWorkspace();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const path = { params: { path: { workspaceId: workspace.id } } };
  const applied = (updated: Workspace) => {
    queryClient.setQueryData(workspaceKey(workspace.id), updated);
    return queryClient.invalidateQueries({ queryKey: [...WORKSPACES_KEY, 'list'] });
  };

  const form = useForm<NameValues>({
    resolver: zodResolver(workspaceNameSchema),
    values: { name: workspace.name },
  });
  const rename = useMutation({
    mutationFn: async (values: NameValues) =>
      unwrap(await api.PATCH('/api/v1/workspaces/{workspaceId}', { ...path, body: values })),
    onSuccess: applied,
  });
  const archive = useMutation({
    mutationFn: async (archived: boolean) =>
      unwrap(
        archived
          ? await api.POST('/api/v1/workspaces/{workspaceId}/archive', path)
          : await api.POST('/api/v1/workspaces/{workspaceId}/restore', path),
      ),
    onSuccess: applied,
  });
  const leave = useMutation({
    mutationFn: async () =>
      ensureOk(await api.POST('/api/v1/workspaces/{workspaceId}/leave', path)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: WORKSPACES_KEY });
      await navigate(paths.workspaces);
    },
  });
  const error = rename.error ?? archive.error ?? leave.error;

  return (
    <div className="space-y-6">
      {can(workspace, 'workspace.rename') && (
        <Card title={t('workspace.rename.title')}>
          <form
            className="flex items-end gap-3"
            onSubmit={form.handleSubmit((values) => rename.mutate(values))}
            noValidate
          >
            <div className="flex-1">
              <TextField control={form.control} name="name" label={t('workspaces.field.name')} />
            </div>
            <Button type="submit" variant="secondary" disabled={rename.isPending}>
              {t('common.save')}
            </Button>
          </form>
        </Card>
      )}
      {can(workspace, 'workspace.archive') && !workspace.isPersonal && (
        <Card title={t('workspace.archive.title')}>
          <p className="text-sm text-muted-foreground">
            {workspace.archived ? t('workspace.archive.restoreHint') : t('workspace.archive.hint')}
          </p>
          <Button
            type="button"
            variant={workspace.archived ? 'secondary' : 'danger'}
            disabled={archive.isPending}
            onClick={() => archive.mutate(!workspace.archived)}
          >
            {workspace.archived ? t('workspace.archive.restore') : t('workspace.archive.submit')}
          </Button>
        </Card>
      )}
      {can(workspace, 'workspace.leave') && !workspace.isPersonal && (
        <Card title={t('workspace.leave.title')}>
          <Button
            type="button"
            variant="danger"
            disabled={leave.isPending}
            onClick={() => leave.mutate()}
          >
            {t('workspace.leave.submit')}
          </Button>
        </Card>
      )}
      {error !== null && <Notice tone="danger">{t(errorMessageKey(error))}</Notice>}
    </div>
  );
}
